/**
 * Export Utilities for Promise to Pay
 * Provides functionality to export promise data to Excel format
 */

import { formatDateDDMMYYYY } from '@/lib/utils';
import { formatCurrency } from '@/lib/utils/formatters';
import type { PromiseToPay } from '@/types/collections';

/**
 * Export promises to Excel (CSV format)
 * @param promises - Array of promises to export
 * @param filename - Optional filename (defaults to promises-{date}.csv)
 */
export function exportPromisesToExcel(promises: PromiseToPay[], filename?: string): void {
  if (promises.length === 0) {
    throw new Error('No promises to export');
  }

  // Define columns to export
  const columns = [
    { key: 'id', label: 'Promise ID' },
    { key: 'customerName', label: 'Customer Name' },
    { key: 'customerAccountNumber', label: 'Account Number' },
    { key: 'caseNumber', label: 'Case Number' },
    { key: 'promiseDate', label: 'Promise Date' },
    { key: 'promisedAmount', label: 'Promised Amount' },
    { key: 'status', label: 'Status' },
    { key: 'daysOverdue', label: 'Days Overdue' },
    { key: 'confirmationMethod', label: 'Confirmation Method' },
    { key: 'customerEmail', label: 'Customer Email' },
    { key: 'customerPhone', label: 'Customer Phone' },
    { key: 'createdBy', label: 'Created By' },
    { key: 'createdAt', label: 'Created Date' },
    { key: 'notes', label: 'Notes' },
    { key: 'invoiceCount', label: 'Number of Transactions' },
    { key: 'isAtRisk', label: 'At Risk' },
    { key: 'brokenPromisesCount', label: 'Broken Promises Count' },
    { key: 'lastActionType', label: 'Last Action Type' },
    { key: 'lastActionDescription', label: 'Last Action Description' },
    { key: 'lastActionDate', label: 'Last Action Date' },
    { key: 'lastActionBy', label: 'Last Action By' },
  ];

  // Create CSV header
  const header = columns.map((col) => `"${col.label}"`).join(',');

  // Create CSV rows
  const rows = promises.map((promise) => {
    return columns
      .map((col) => {
        let value: string | number | boolean | undefined | null = '';

        // Map the data based on column key
        switch (col.key) {
          case 'id':
            value = promise.id;
            break;
          case 'customerName':
            value = promise.customerName;
            break;
          case 'customerAccountNumber':
            value = promise.customerAccountNumber;
            break;
          case 'caseNumber':
            value = promise.caseNumber;
            break;
          case 'promiseDate':
            value = formatDateDDMMYYYY(promise.promiseDate.toString());
            break;
          case 'promisedAmount':
            value = formatCurrency(promise.promisedAmount);
            break;
          case 'status':
            value = promise.status;
            break;
          case 'daysOverdue':
            value = promise.daysOverdue && promise.daysOverdue > 0 ? promise.daysOverdue : '';
            break;
          case 'confirmationMethod':
            value = promise.confirmationMethod;
            break;
          case 'customerEmail':
            value = promise.customerEmail || '';
            break;
          case 'customerPhone':
            value = promise.customerPhone || '';
            break;
          case 'createdBy':
            value = promise.createdBy;
            break;
          case 'createdAt':
            value = promise.createdAt ? formatDateDDMMYYYY(promise.createdAt.toString()) : '';
            break;
          case 'notes':
            value = promise.notes || '';
            break;
          case 'invoiceCount':
            value = promise.invoiceCount ?? 0;
            break;
          case 'isAtRisk':
            value = promise.isAtRisk ? 'Yes' : 'No';
            break;
          case 'brokenPromisesCount':
            value = promise.brokenPromisesCount || 0;
            break;
          case 'lastActionType':
            value = promise.lastAction ? promise.lastAction.type.replace('_', ' ').toUpperCase() : '';
            break;
          case 'lastActionDescription':
            value = promise.lastAction ? promise.lastAction.description : '';
            break;
          case 'lastActionDate':
            value = promise.lastAction ? formatDateDDMMYYYY(promise.lastAction.date.toString()) : '';
            break;
          case 'lastActionBy':
            value = promise.lastAction ? promise.lastAction.performedBy : '';
            break;
          default:
            value = '';
        }

        // Handle null/undefined
        if (value === undefined || value === null) {
          value = '';
        }

        // Escape quotes and wrap in quotes
        return `"${String(value).replace(/"/g, '""')}"`;
      })
      .join(',');
  });

  // Combine header and rows
  const csv = [header, ...rows].join('\n');

  // Create blob and download
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename || `promises-to-pay-${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
